import apiClient from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LexicalWord {
  id: number;
  surfaceForm: string;
  lemma: string | null; // null when same as surface form
  language: string;
  partOfSpeech: string | null;
  createdAt: string;
}

export interface LexicalWordWithExposure extends LexicalWord {
  totalOccurrences: number;
  segmentCount: number;
  isVocabulary: boolean;
}

export interface ExposureDistribution {
  single: number;   // exactly 1 occurrence
  low: number;      // 2–5 occurrences
  building: number; // 6–19 occurrences
  strong: number;   // 20+ occurrences
}

export interface VocabBucketStats {
  single: number;    // 1 exposure
  low: number;       // 2–5 exposures
  building: number;  // 6–19 exposures
  strong: number;    // 20+ exposures
  phrases: number;   // multi-token; cannot match lexical tokens
  notMapped: number; // single-token but no exposure record
}

export interface LexicalStats {
  totalUniqueWords: number;
  totalWordOccurrences: number;
  topWords: LexicalWordWithExposure[];
  lowExposureWords: LexicalWordWithExposure[];
  vocabularyInLexicon: number;
  exposureBuckets: ExposureDistribution;
  vocabDistribution: VocabBucketStats;
}

export interface BackfillResult {
  segmentsProcessed: number;
  wordsUpserted: number;
  errors: number;
}

export interface GetLexicalWordsParams {
  language?: string;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const lexicalService = {
  /**
   * Get aggregate word exposure statistics for the current user.
   * Pass language to filter by project language (e.g. "Japanese").
   */
  async getStats(language?: string): Promise<LexicalStats> {
    const params: Record<string, string> = {};
    if (language) params.language = language;
    const { data } = await apiClient.get<LexicalStats>('/lexical/stats', { params });
    return data;
  },

  /**
   * Get a paginated list of words the user has been exposed to,
   * ordered by total occurrences descending.
   */
  async getWords(params?: GetLexicalWordsParams): Promise<LexicalWordWithExposure[]> {
    const queryParams: Record<string, string | number> = {};
    if (params?.language) queryParams.language = params.language;
    if (params?.limit) queryParams.limit = params.limit;
    if (params?.offset) queryParams.offset = params.offset;
    const { data } = await apiClient.get<LexicalWordWithExposure[]>('/lexical/words', {
      params: queryParams,
    });
    return data;
  },

  /**
   * Get the distinct languages present in the current user's lexical data.
   */
  async getLanguages(): Promise<string[]> {
    const { data } = await apiClient.get<string[]>('/lexical/languages');
    return data;
  },

  /**
   * Trigger a backfill of all existing transcripts for the user.
   * Idempotent — safe to run multiple times.
   * Pass language to limit backfill to a single project language.
   */
  async backfill(language?: string): Promise<BackfillResult> {
    const { data } = await apiClient.post<BackfillResult>('/lexical/backfill', {
      language: language ?? '',
    });
    return data;
  },
};
